import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { studio, apiError, inputCls, btnPrimary, TRADE_OPTIONS, VAT_COUNTRIES, PAYMENT_TERMS } from "@/lib/studioApi";
import { loadFiscal, resetFiscal } from "@/lib/fiscal";
import { DocumentBranding } from "@/components/DocumentBranding";
import { MollieSettings } from "@/components/studio/MollieSettings";
import { ThemeSettings } from "@/components/studio/ThemeSettings";
import { AiEngineSettings } from "@/components/studio/AiEngineSettings";
import { ReminderSettings } from "@/components/studio/ReminderSettings";
import { PageSkeleton } from "@/components/studio/StudioUI";
import { SecuritySettings } from "@/components/studio/SecuritySettings";

export default function StudioProfile() {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fiscalRules, setFiscalRules] = useState(null);

  useEffect(() => {
    studio.get("/profile").then(({ data }) => setProfile(data)).catch(() => toast.error("Profil indisponible."));
    loadFiscal().then(setFiscalRules).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await studio.put("/profile", {
        ...profile,
        quote_validity_days: Number(profile.quote_validity_days) || 30,
        target_margin_percent: Number(profile.target_margin_percent) || 0,
        hourly_cost: Number(profile.hourly_cost) || 0,
      });
      toast.success("Profil enregistré — il apparaîtra sur vos devis.");
      resetFiscal();
      loadFiscal().then((f) => setFiscalRules(f)).catch(() => {});
      const { data: who } = await studio.get("/whoami");
      if ((who.lang || "fr") !== (localStorage.getItem("devisto_app_lang") || "fr")) { localStorage.setItem("devisto_app_lang", who.lang || "fr"); window.location.reload(); }
    } catch (e) { toast.error(apiError(e, "Enregistrement impossible.")); } finally { setSaving(false); }
  };

  if (!profile) return <StudioLayout title="Mon entreprise"><PageSkeleton /></StudioLayout>;

  const set = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));
  const setTax = (k) => (e) => setProfile((p) => ({ ...p, tax_ids: { ...(p.tax_ids || {}), [k]: e.target.value } }));
  const selectedCountry = profile.country || profile.vat_country || "BE";
  const taxFields = (fiscalRules?.countries || []).find((c) => c.country === selectedCountry)?.tax_ids || [];

  return (
    <StudioLayout title="Mon entreprise" subtitle="Ces informations apparaissent en en-tête de vos devis PDF.">
      <div data-testid="studio-profile-form" className="mt-6 rounded-2xl border border-[#E2E7F7] bg-white p-5 shadow-[0_1px_2px_rgba(15,38,69,0.04)] space-y-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Nom de l'entreprise</span><input data-testid="profile-company" className={inputCls} placeholder="Nom de l'entreprise" value={profile.company || ""} onChange={set("company")} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Personne de contact</span><input data-testid="profile-contact" className={inputCls} placeholder="Personne de contact" value={profile.contact_name || ""} onChange={set("contact_name")} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">N° de TVA</span><input data-testid="profile-vat" className={inputCls} placeholder="N° de TVA (BE 0123.456.789)" value={profile.vat || ""} onChange={set("vat")} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Téléphone</span><input data-testid="profile-phone" className={inputCls} placeholder="Téléphone" value={profile.phone || ""} onChange={set("phone")} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">E-mail</span><input data-testid="profile-email" className={inputCls} placeholder="Email" value={profile.email || ""} onChange={set("email")} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">IBAN</span><input data-testid="profile-iban" className={inputCls} placeholder="IBAN" value={profile.iban || ""} onChange={set("iban")} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Rue et numéro</span><input data-testid="profile-address" className={inputCls} placeholder="Rue et numéro" value={profile.address || ""} onChange={set("address")} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Code postal</span><input data-testid="profile-postal" className={inputCls} placeholder="Code postal" value={profile.postal_code || ""} onChange={set("postal_code")} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Commune</span><input data-testid="profile-city" className={inputCls} placeholder="Commune" value={profile.city || ""} onChange={set("city")} /></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Métier principal</span><select data-testid="profile-trade" className={inputCls} value={profile.trade || "chauffagiste"} onChange={set("trade")}>
            {TRADE_OPTIONS.map(([v, l]) => <option key={v} value={v} >{l}</option>)}
          </select></label>
          <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Validité des devis (jours)</span><input data-testid="profile-validity" type="number" className={inputCls} placeholder="Validité des devis (jours)" value={profile.quote_validity_days ?? 30} onChange={set("quote_validity_days")} /></label>
        </div>
        <label className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Conditions par défaut de vos devis</span><textarea data-testid="profile-conditions" rows={3} className={inputCls} placeholder="Ex. acompte de 30 % à la commande, solde à la réception…" value={profile.quote_conditions || ""} onChange={set("quote_conditions")} /></label>

        {taxFields.length > 0 && (
          <div data-testid="profile-tax-ids" className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
            <p className="text-sm font-semibold text-[#0F2645]">Identifiants fiscaux · {fiscalRules?.country || "BE"}</p>
            <p className="mt-0.5 text-xs text-[#5C6A87]">Imprimés sur vos devis et factures selon les mentions obligatoires de votre pays. Devise : {fiscalRules?.currency || "EUR"}.</p>
            <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {taxFields.map((t) => (
                <label key={t.key} className="block"><span className="mb-1 block text-xs font-semibold text-[#5C6A87]">{t.label}{t.required ? " *" : ""}</span>
                  {t.type === "select" ? (
                    <select data-testid={`profile-tax-${t.key}`} className={inputCls} value={(profile.tax_ids || {})[t.key] || ""} onChange={setTax(t.key)}>
                      <option value="">— choisir</option>
                      {(t.options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                    </select>
                  ) : (
                    <input data-testid={`profile-tax-${t.key}`} className={`${inputCls} font-mono`} placeholder={t.placeholder || ""} value={(profile.tax_ids || {})[t.key] || ""} onChange={setTax(t.key)} />
                  )}
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
          <p className="text-sm font-semibold text-[#0F2645]">Facturation et avis clients</p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Pays de l'entreprise (devise & TVA)</span>
              <select data-testid="profile-vat-country" className={`${inputCls} mt-1`} value={profile.country || profile.vat_country || "BE"} onChange={(e) => { setProfile((p) => ({ ...p, country: e.target.value, vat_country: e.target.value })); resetFiscal(); }}>
                {(fiscalRules?.countries?.length ? fiscalRules.countries.map((c) => [c.country, `${c.flag} ${c.name} · ${c.currency}`]) : VAT_COUNTRIES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
            {selectedCountry === "BE" && (
            <label className="block" data-testid="profile-app-lang-field">
              <span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Langue de l'application et des documents</span>
              <select data-testid="profile-app-lang" data-no-translate className={`${inputCls} mt-1`} value={profile.app_lang || "fr"} onChange={set("app_lang")}>
                <option value="fr">Français</option>
                <option value="nl">Nederlands</option>
              </select>
              <span className="mt-1 block text-[11px] text-[#93A0BC]">Menus, devis, factures et e-mails clients dans cette langue après enregistrement.</span>
            </label>
            )}
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Délai de paiement</span>
              <select data-testid="profile-payment-terms" className={`${inputCls} mt-1`} value={profile.payment_terms || "15"} onChange={set("payment_terms")}>
                {PAYMENT_TERMS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </label>
          </div>
          <label className="mt-3 flex items-start gap-3 rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="profile-invoice-reminders"
              className="mt-0.5 h-4 w-4 accent-[#3A5CC8]"
              checked={profile.invoice_reminders !== false}
              onChange={(e) => setProfile((p) => ({ ...p, invoice_reminders: e.target.checked }))}
            />
            <span className="text-xs text-[#5C6A87] leading-relaxed">
              <span className="block text-sm font-semibold text-[#0F2645]">Relancer automatiquement les factures impayées</span>
              Jusqu'à 3 rappels envoyés au client après l'échéance, au rythme et au ton que vous choisissez ; vous êtes prévenu à chaque envoi.
            </span>
          </label>
          {profile.invoice_reminders !== false && <ReminderSettings profile={profile} setProfile={setProfile} />}

          <label className="mt-3 flex items-start gap-3 rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="profile-quote-reminders"
              className="mt-0.5 h-4 w-4 accent-[#3A5CC8]"
              checked={profile.quote_reminders !== false}
              onChange={(e) => setProfile((p) => ({ ...p, quote_reminders: e.target.checked }))}
            />
            <span className="text-xs text-[#5C6A87] leading-relaxed">
              <span className="block text-sm font-semibold text-[#0F2645]">Relancer automatiquement les devis sans réponse</span>
              Le client reçoit un rappel 7 puis 14 jours après l'envoi du devis tant qu'il n'a pas signé ou refusé.
            </span>
          </label>
          <label className="mt-3 flex items-start gap-3 rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="profile-signature-reminders"
              className="mt-0.5 h-4 w-4 accent-[#3A5CC8]"
              checked={profile.signature_reminders !== false}
              onChange={(e) => setProfile((p) => ({ ...p, signature_reminders: e.target.checked }))}
            />
            <span className="text-xs text-[#5C6A87] leading-relaxed">
              <span className="block text-sm font-semibold text-[#0F2645]">Relancer les rapports d'intervention non signés</span>
              Rappel au client 3 puis 7 jours après l'envoi du lien de signature ; à la dernière relance, vous êtes prévenu pour l'appeler.
            </span>
          </label>

          <label className="mt-3 flex items-start gap-3 rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="profile-review-after-intervention"
              className="mt-0.5 h-4 w-4 accent-[#3A5CC8]"
              checked={profile.review_after_intervention !== false}
              onChange={(e) => setProfile((p) => ({ ...p, review_after_intervention: e.target.checked }))}
            />
            <span className="text-xs text-[#5C6A87] leading-relaxed">
              <span className="block text-sm font-semibold text-[#0F2645]">Demander un avis après chaque intervention</span>
              Dès que le rapport est signé, le client reçoit un e-mail pour noter l'intervention (1 à 5 étoiles). À partir de 4 étoiles, il est invité à laisser un avis Google via le lien ci-dessous ; en dessous, son commentaire vous est transmis en privé.
            </span>
          </label>

          <label className="mt-3 flex items-start gap-3 rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 cursor-pointer">
            <input
              type="checkbox"
              data-testid="profile-reviews-on-quotes"
              className="mt-0.5 h-4 w-4 accent-[#3A5CC8]"
              checked={profile.reviews_on_quotes !== false}
              onChange={(e) => setProfile((p) => ({ ...p, reviews_on_quotes: e.target.checked }))}
            />
            <span className="text-xs text-[#5C6A87] leading-relaxed">
              <span className="block text-sm font-semibold text-[#0F2645]">Afficher mes avis clients dans les devis</span>
              Note moyenne et meilleurs commentaires (≥ 4 étoiles) affichés sur la page de signature et dans le PDF du devis : la preuve sociale au moment de décider.
            </span>
          </label>

          <label className="mt-3 block">
            <span className="mb-1 block text-xs font-semibold text-[#5C6A87]">Lien « Laisser un avis Google »</span>
            <input data-testid="profile-review-url" className={inputCls} placeholder="https://g.page/r/… (lien long, pas de lien raccourci)" value={profile.review_url || ""} onChange={set("review_url")} />
            <span className="mt-1 block text-xs text-[#5C6A87] leading-relaxed">
              Utilisé dans l'e-mail de rapport et sur la page d'avis après intervention.
            </span>
          </label>
        </div>

        <AiEngineSettings value={profile.ai_engine} onChange={(v) => setProfile((p) => ({ ...p, ai_engine: v }))} />

        <ThemeSettings />

        <MollieSettings />

        <div data-testid="profile-insurance" className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
          <p className="text-sm font-semibold text-[#0F2645]">Mon assurance</p>
          <p className="mt-1 text-xs text-[#5C6A87] leading-relaxed">
            Rassurez vos clients : les coordonnées de votre assurance apparaissent en bas de vos devis et factures.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Nom de l'assureur</span>
              <input data-testid="profile-insurer-name" className={`${inputCls} mt-1`} placeholder="AXA, Ethias, AG, Baloise…" value={profile.insurer_name || ""} onChange={set("insurer_name")} />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Adresse de l'assureur</span>
              <input data-testid="profile-insurer-address" className={`${inputCls} mt-1`} placeholder="Rue, code postal, ville" value={profile.insurer_address || ""} onChange={set("insurer_address")} />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Type de garantie</span>
              <select data-testid="profile-insurance-type" className={`${inputCls} mt-1`} value={profile.insurance_type || ""} onChange={set("insurance_type")}>
                <option value="">Sélectionner la garantie</option>
                <option value="rc_pro">RC professionnelle</option>
                <option value="rc_exploitation">RC exploitation</option>
                <option value="decennale">Garantie décennale</option>
                <option value="tous_risques_chantier">Tous risques chantier</option>
                <option value="autre">Autre</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Numéro de contrat</span>
              <input data-testid="profile-insurance-contract" className={`${inputCls} mt-1 font-mono`} placeholder="AV123456" value={profile.insurance_contract || ""} onChange={set("insurance_contract")} />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Zone de couverture</span>
              <input data-testid="profile-insurance-zone" className={`${inputCls} mt-1`} placeholder="Belgique, Benelux…" value={profile.insurance_zone || ""} onChange={set("insurance_zone")} />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Valable jusqu'au</span>
              <input data-testid="profile-insurance-valid" type="date" className={`${inputCls} mt-1`} value={profile.insurance_valid_until || ""} onChange={set("insurance_valid_until")} />
            </label>
          </div>
          <label className="mt-3 flex items-start gap-3 rounded-xl border border-[#E2E7F7] bg-white px-4 py-3 cursor-pointer">
            <input type="checkbox" data-testid="profile-insurance-on-docs" checked={profile.insurance_on_documents !== false} onChange={(e) => setProfile((p) => ({ ...p, insurance_on_documents: e.target.checked }))} className="mt-0.5 h-4 w-4 rounded accent-[#3A5CC8]" />
            <span className="text-xs text-[#5C6A87] leading-relaxed">
              <span className="block text-sm font-semibold text-[#0F2645]">Afficher l'assurance sur mes documents</span>
              Ligne « Assurance {({ rc_pro: "RC professionnelle", rc_exploitation: "RC exploitation", decennale: "Garantie décennale", tous_risques_chantier: "Tous risques chantier", autre: "Autre" })[profile.insurance_type] || "professionnelle"} : {profile.insurer_name || "votre assureur"} – contrat n° … » en pied de page des devis et factures PDF.
            </span>
          </label>
          {selectedCountry === "BE" && <div data-testid="profile-insurance-info" className="mt-3 rounded-xl border border-[#C9D4F4] bg-[#EEF1FC] px-4 py-3 text-xs leading-relaxed text-[#2F4CA8]">
            <span className="font-bold">Pas encore assuré ?</span> En Belgique, la RC professionnelle couvre les dommages causés chez vos clients, et
            l'assurance décennale est obligatoire (loi Peeters-Borsus) pour les travaux de gros œuvre sur habitations. Demandez une offre à votre
            courtier ou comparez les assureurs belges (AXA, Ethias, AG, Baloise, Federale).
          </div>}
        </div>

        <div className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
          <p className="text-sm font-semibold text-[#0F2645]">Ma rentabilité</p>
          <p className="mt-1 text-xs text-[#5C6A87] leading-relaxed">
            Sert à calculer la marge et le temps de main-d'œuvre affichés sur vos devis avant l'envoi. Ces chiffres restent
            privés : ils n'apparaissent ni sur le PDF client ni dans les emails.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Marge minimum souhaitée (%)</span>
              <input data-testid="profile-target-margin" type="number" step="1" min="0" max="95" className={`${inputCls} mt-1`} value={profile.target_margin_percent ?? 25} onChange={set("target_margin_percent")} />
            </label>
            <label className="block">
              <span className="text-[11px] font-bold uppercase tracking-wide text-[#5C6A87]">Coût horaire de mon ouvrier (€/h)</span>
              <input data-testid="profile-hourly-cost" type="number" step="0.5" min="0" className={`${inputCls} mt-1`} value={profile.hourly_cost ?? 45} onChange={set("hourly_cost")} />
            </label>
          </div>
        </div>
        <DocumentBranding profile={profile} setProfile={setProfile} />

        <div className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
          <p className="text-sm font-semibold text-[#0F2645]">Mon logiciel de facturation (Billit)</p>
          <p className="mt-1 text-xs text-[#5C6A87] leading-relaxed">
            Collez votre clé API et votre PartyID Billit (MyBillit → Intégrations) : vos devis acceptés partiront directement en facture, sans copier-coller.
            Laissez l'URL sandbox pour tester, puis passez à l'URL de production.
          </p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input data-testid="profile-billit-key" type="password" className={inputCls} placeholder="Clé API Billit" value={profile.billit_api_key || ""} onChange={set("billit_api_key")} />
            <input data-testid="profile-billit-party" className={inputCls} placeholder="PartyID Billit" value={profile.billit_party_id || ""} onChange={set("billit_party_id")} />
            <select data-testid="profile-billit-url" className={inputCls} value={profile.billit_base_url || "https://api.sandbox.billit.be"} onChange={set("billit_base_url")}>
              <option value="https://api.sandbox.billit.be">Environnement de test (sandbox)</option>
              <option value="https://api.billit.be">Production</option>
            </select>
          </div>
        </div>

        <div className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
          <p className="text-sm font-semibold text-[#0F2645]">Yuki</p>
          <p className="mt-1 text-xs text-[#5C6A87]">Clé d'accès Web Service Yuki + identifiant d'administration (import automatique des ventes activé).</p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="profile-yuki-key" type="password" className={inputCls} placeholder="Clé d'accès Yuki" value={profile.yuki_access_key || ""} onChange={set("yuki_access_key")} />
            <input data-testid="profile-yuki-admin" className={inputCls} placeholder="Administration ID Yuki" value={profile.yuki_administration_id || ""} onChange={set("yuki_administration_id")} />
          </div>
        </div>

        <div className="rounded-xl border border-[#E2E7F7] bg-[#F6F8FD] p-4">
          <p className="text-sm font-semibold text-[#0F2645]">Odoo</p>
          <p className="mt-1 text-xs text-[#5C6A87]">Clé API à générer dans Odoo → Préférences → Sécurité du compte → Nouvelle clé API. La facture est créée en brouillon, vous la validez dans Odoo.</p>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input data-testid="profile-odoo-url" className={inputCls} placeholder="https://mon-instance.odoo.com" value={profile.odoo_url || ""} onChange={set("odoo_url")} />
            <input data-testid="profile-odoo-db" className={inputCls} placeholder="Nom de la base" value={profile.odoo_db || ""} onChange={set("odoo_db")} />
            <input data-testid="profile-odoo-login" className={inputCls} placeholder="Login Odoo" value={profile.odoo_login || ""} onChange={set("odoo_login")} />
            <input data-testid="profile-odoo-key" type="password" className={inputCls} placeholder="Clé API Odoo" value={profile.odoo_api_key || ""} onChange={set("odoo_api_key")} />
          </div>
        </div>

        <button type="button" data-testid="profile-save-btn" disabled={saving} onClick={save} className={btnPrimary}>
          <Save className="w-4 h-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
        </button>

        <SecuritySettings />
      </div>
    </StudioLayout>
  );
}
